import React from "react";
import { Leaf, Target, TrendingDown, Award } from "lucide-react";

interface CarbonTrackerIntroModalProps {
  open: boolean;
  onClose: () => void;
  onGetStarted: () => void;
}

const CarbonTrackerIntroModal: React.FC<CarbonTrackerIntroModalProps> = ({
  open,
  onClose,
  onGetStarted,
}) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="dark:bg-[#1c1c1c] bg-white rounded-3xl p-8 shadow-2xl w-full max-w-lg relative transform transition-all">
        <button
          className="absolute top-4 right-4 dark:text-white text-gray-600 text-xl hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full w-8 h-8 flex items-center justify-center transition-colors"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <div className="text-center">
          {/* Hero Icon */}
          <div className="flex justify-center mb-6">
            <div className="bg-[#40e0d0]/10 p-4 rounded-full">
              <Leaf className="w-12 h-12 text-[#40e0d0]" />
            </div>
          </div>

          {/* Main Heading */}
          <h2 className="text-3xl font-bold dark:text-white text-gray-900 mb-3">
            Start Your Carbon Journey
          </h2>

          {/* Subheading */}
          <p className="text-lg dark:text-gray-300 text-gray-600 mb-8 leading-relaxed">
            Take control of your environmental impact and build sustainable
            habits that make a real difference.
          </p>

          {/* Benefits Grid */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="text-center p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
              <Target className="w-6 h-6 text-[#40e0d0] mx-auto mb-2" />
              <h3 className="font-semibold text-sm dark:text-white text-gray-900 mb-1">
                Set Goals
              </h3>
              <p className="text-xs dark:text-gray-400 text-gray-600">
                Track progress toward your emission targets
              </p>
            </div>

            <div className="text-center p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
              <TrendingDown className="w-6 h-6 text-[#40e0d0] mx-auto mb-2" />
              <h3 className="font-semibold text-sm dark:text-white text-gray-900 mb-1">
                Reduce Impact
              </h3>
              <p className="text-xs dark:text-gray-400 text-gray-600">
                Get personalized tips to lower emissions
              </p>
            </div>

            <div className="text-center p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
              <Award className="w-6 h-6 text-[#40e0d0] mx-auto mb-2" />
              <h3 className="font-semibold text-sm dark:text-white text-gray-900 mb-1">
                Earn Rewards
              </h3>
              <p className="text-xs dark:text-gray-400 text-gray-600">
                Celebrate your sustainability wins
              </p>
            </div>

            <div className="text-center p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
              <Leaf className="w-6 h-6 text-[#40e0d0] mx-auto mb-2" />
              <h3 className="font-semibold text-sm dark:text-white text-gray-900 mb-1">
                Make Impact
              </h3>
              <p className="text-xs dark:text-gray-400 text-gray-600">
                Join a community of eco-conscious users
              </p>
            </div>
          </div>

          {/* Call to Action */}
          <button
            className="w-full py-4 rounded-xl bg-[#40e0d0] hover:bg-[#36c7b7] text-black font-semibold text-lg transition-colors shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
            onClick={onGetStarted}
          >
            Start Tracking My Impact
          </button>

          {/* Trust indicator */}
          <p className="text-xs dark:text-gray-500 text-gray-500 mt-4">
            Join thousands of users reducing their carbon footprint 🌱
          </p>
        </div>
      </div>
    </div>
  );
};

export default CarbonTrackerIntroModal;
